import React, { useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import LandingMarketing from '../components/LandingMarketing'
import DashboardHomeContent from '../components/DashboardHomeContent'
import { useTranslation } from '../hooks/use-i18n'
import LanguageSelector from '../components/LanguageSelector'

const Landing: React.FC = () => {
  const { t } = useTranslation()
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const menuRef = useRef<HTMLElement>(null)
  const navigate = useNavigate()

  const handleLogoClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [isAuthenticated, navigate])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen])

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#0f1623' }}>
      <header ref={menuRef} className="border-b border-white/10 fixed top-0 left-0 right-0 z-50 w-full" style={{ backgroundColor: '#0f1623' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 w-full">
          <div className="flex justify-between items-center gap-2 py-3 sm:py-4 min-h-[56px] sm:min-h-[64px]">
            <button
              onClick={handleLogoClick}
              className="flex items-center shrink-0 min-w-0 transition-all duration-300 cursor-pointer"
              aria-label={isAuthenticated ? t('common.goToDashboard') : t('common.goToTop')}
            >
              <img src="/assets/restoside_only_logo.png" alt={t('common.logoAlt')} className="h-7 sm:h-8 w-auto max-w-[7rem] sm:max-w-none lg:hidden object-contain" />
              <img src="/assets/restoside_with_logo.png" alt={t('common.logoAlt')} className="h-8 sm:h-9 w-auto hidden lg:block object-contain" />
            </button>

            {!isAuthenticated && (
              <div className="hidden lg:flex items-center space-x-8">
                <a href="#features" className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                  {t('nav.features')}
                </a>
                <Link to="/pricing" className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                  {t('nav.pricing')}
                </Link>
              </div>
            )}

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <div className="hidden md:block">
                <LanguageSelector />
              </div>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="text-[#0f1623] px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap"
                  style={{ backgroundColor: '#f59e0b' }}
                >
                  {t('nav.dashboard')}
                </Link>
              ) : (
                <>
                  <Link to="/login" className="hidden lg:block text-white/80 hover:text-white text-sm font-medium whitespace-nowrap">
                    {t('common.login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="hidden md:inline-flex text-[#0f1623] px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap"
                    style={{ backgroundColor: '#f59e0b' }}
                  >
                    {t('common.startForFree')}
                  </Link>
                </>
              )}
              {!isAuthenticated && (
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden text-white p-2 shrink-0"
                  aria-label={t('common.toggleMobileMenu')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isMobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              )}
            </div>
          </div>

          {isMobileMenuOpen && !isAuthenticated && (
            <div className="lg:hidden border-t border-white/10 py-4">
              <div className="flex flex-col space-y-3">
                <div className="px-1">
                  <p className="text-xs font-medium text-white/50 uppercase tracking-wide mb-2 px-2">{t('common.language')}</p>
                  <LanguageSelector variant="inline" />
                </div>
                <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 text-white/80">
                  {t('nav.features')}
                </a>
                <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 text-white/80">
                  {t('nav.pricing')}
                </Link>
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="px-3 py-2 text-white/80">
                  {t('common.login')}
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mx-3 mt-1 text-center py-3 rounded-full font-semibold text-[#0f1623]"
                  style={{ backgroundColor: '#f59e0b' }}
                >
                  {t('common.startForFree')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 pt-[4.5rem] sm:pt-24">
        {isAuthenticated ? <DashboardHomeContent /> : <LandingMarketing />}
      </main>

      <footer className="border-t border-white/10 mt-8" style={{ backgroundColor: '#0f1623' }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
            <p className="text-white/50 text-sm">{t('common.copyright')}</p>
            <div className="flex gap-6 text-sm">
              <Link to="/pricing" className="text-white/50 hover:text-white">{t('nav.pricing')}</Link>
              <a href="mailto:sales@restoside.com" className="text-white/50 hover:text-white">{t('common.contactSales')}</a>
            </div>
          </div>
          <p className="text-white/35 text-xs text-center mt-4">{t('common.footerTagline')}</p>
        </div>
      </footer>
    </div>
  )
}

export default Landing
